import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Select, Tag, Typography } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import type { InputRef } from 'antd';
import { UserListInterface } from '../../interfaces/User.interface.ts';
import {
	useResolveOperatorPinMutation,
	ResolveOperatorPinResponse,
} from '../../redux/api/users.ts';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const { Text } = Typography;

interface IProps {
	/** Operators for the emergency fallback selector (Machine Operators group). */
	userData: UserListInterface[];
	/** Currently resolved operator user id, if any. */
	value?: string | number;
	/** Called with the resolved operator (or null when cleared). */
	onResolved: (
		userId: number | undefined,
		info: ResolveOperatorPinResponse | null,
		pin?: string
	) => void;
	/** Marks the field as touched so the parent can show required-state errors. */
	showError?: boolean;
	/**
	 * Role group(s) this screen records (e.g. `['Keyence Operators']`). When
	 * set, the server refuses a PIN whose operator is outside this role and
	 * narrows the emergency selector to it, so a valid PIN from another role
	 * can't be attributed here. Omit to keep the group-agnostic behaviour.
	 */
	expectedGroups?: string[];
	/** Field caption, for flows that attribute a receiver instead of an operator. */
	label?: string;
	/** Blocks identification when the parent screen is read-only. */
	disabled?: boolean;
	/**
	 * Layout style. `card` (default) is the full-height boxed field used in
	 * modals/forms. `inline` is a compact single-row layout for dense toolbars
	 * (e.g. the QC inspection-results header) where vertical space is scarce.
	 */
	variant?: 'card' | 'inline';
	/**
	 * Auto-focuses the PIN input on mount. Safe for single-instance contexts
	 * (modals/single cards). Set to `false` when many of these fields render in
	 * a scrollable list (e.g. the Keyence queue): each card focusing on mount
	 * scrolls itself into view, so the page fights to the bottom and the focus
	 * visibly flashes between inputs.
	 */
	autoFocus?: boolean;
}

const PIN_LENGTH = 4;

/**
 * Identifies the operator by PIN instead of a growing name dropdown. SFA owns
 * the PINs; this only resolves a typed PIN to an operator via the ERP mirror.
 * A "Forgot PIN?" path keeps working via the emergency PIN + explicit operator.
 */
const OperatorPinField: React.FC<IProps> = ({
	userData,
	value,
	onResolved,
	showError,
	expectedGroups,
	label = 'Operator',
	disabled = false,
	variant = 'card',
	autoFocus = true,
}) => {
	const [pin, setPin] = useState<string>('');
	const [resolved, setResolved] =
		useState<ResolveOperatorPinResponse | null>(null);
	const [error, setError] = useState<string>('');
	const [emergencyMode, setEmergencyMode] = useState<boolean>(false);
	const [emergencyOperator, setEmergencyOperator] = useState<
		number | string | undefined
	>(undefined);
	const inputRef = useRef<InputRef>(null);

	const [resolveOperatorPin, { isLoading }] = useResolveOperatorPinMutation();

	useEffect(() => {
		if (autoFocus) {
			inputRef.current?.focus();
		}
	}, [emergencyMode, autoFocus]);

	// Reset local state if the parent clears the selection externally.
	useEffect(() => {
		if (!value) {
			setResolved(null);
		}
	}, [value]);

	const applyResolution = (res: ResolveOperatorPinResponse, typedPin: string) => {
		setResolved(res);
		setError('');
		onResolved(res.user_id, res, typedPin);
	};

	const doResolve = async (rawPin: string, operatorUserId?: number | string) => {
		try {
			const res = await resolveOperatorPin({
				pin: rawPin,
				...(operatorUserId ? { operator_user_id: operatorUserId } : {}),
				...(expectedGroups && expectedGroups.length
					? { expected_groups: expectedGroups }
					: {}),
			}).unwrap();
			applyResolution(res, rawPin);
			setPin('');
		} catch (err: any) {
			setPin('');
			const requiresOperator = err?.data?.emergency_requires_operator;
			if (requiresOperator) {
				setEmergencyMode(true);
				setError('Emergency PIN: select the operator, then re-enter it.');
				return;
			}
			setError(getApiErrorMessage(err) || 'No operator matches that PIN.');
		}
	};

	const handlePinChange = (raw: string) => {
		const digits = raw.replace(/\D/g, '').slice(0, PIN_LENGTH);
		setPin(digits);
		setError('');
		if (digits.length === PIN_LENGTH) {
			doResolve(
				digits,
				emergencyMode ? emergencyOperator : undefined
			);
		}
	};

	const clearOperator = () => {
		setResolved(null);
		setPin('');
		setError('');
		setEmergencyMode(false);
		setEmergencyOperator(undefined);
		onResolved(undefined, null);
	};

	const isInline = variant === 'inline';

	if (isInline) {
		if (resolved && value) {
			return (
				<div className="flex items-center gap-2">
					<Text strong style={{ fontSize: 15 }}>
						{label}:
					</Text>
					<CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
					<Text strong style={{ fontSize: 15 }}>
						{resolved.full_name}
					</Text>
					{resolved.emergency_used && <Tag color="orange">Emergency</Tag>}
					{resolved.must_rotate_pin && (
						<Tag color="warning">Rotate PIN</Tag>
					)}
					<Button size="small" onClick={clearOperator} disabled={disabled}>
						Change
					</Button>
				</div>
			);
		}

		return (
			<div className="flex items-center gap-2">
				<Text strong style={{ fontSize: 15 }}>
					{label} <Text type="danger">*</Text>
				</Text>
				{emergencyMode && (
					<Select
						value={emergencyOperator}
						onChange={setEmergencyOperator}
						size="large"
						disabled={disabled}
						placeholder="Select operator"
						showSearch
						optionFilterProp="label"
						options={userData.map((user) => ({
							value: user.id,
							label: user.full_name || user.first_name,
						}))}
						style={{ minWidth: 180 }}
					/>
				)}
				<Input.Password
					ref={inputRef}
					value={pin}
					onChange={(e) => handlePinChange(e.target.value)}
					size="large"
					inputMode="numeric"
					maxLength={PIN_LENGTH}
					placeholder="••••"
					name="operator-pin"
					autoComplete="new-password"
					data-lpignore="true"
					data-1p-ignore="true"
					disabled={
						disabled || isLoading || (emergencyMode && !emergencyOperator)
					}
					status={error || (showError && !value) ? 'error' : ''}
					style={{ width: 120, letterSpacing: 8, fontSize: 20 }}
				/>
				{(error || (showError && !value)) && (
					<Text type="danger" style={{ fontSize: 12, maxWidth: 200 }}>
						{error || 'Identify the operator to continue'}
					</Text>
				)}
				{emergencyMode ? (
					<Button
						type="link"
						size="small"
						disabled={disabled}
						onClick={clearOperator}
					>
						Back to PIN
					</Button>
				) : (
					<Button
						type="link"
						size="small"
						disabled={disabled}
						onClick={() => {
							setEmergencyMode(true);
							setError('');
							setPin('');
						}}
					>
						Forgot PIN?
					</Button>
				)}
			</div>
		);
	}

	if (resolved && value) {
		return (
			<div className="flex flex-col gap-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
				<Text strong style={{ fontSize: '14px' }}>
					{label} <Text type="danger">*</Text>
				</Text>
				<div className="flex items-center justify-between">
					<span className="flex items-center gap-2">
						<CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
						<Text strong style={{ fontSize: 16 }}>
							{resolved.full_name}
						</Text>
						{resolved.emergency_used && (
							<Tag color="orange">Emergency PIN</Tag>
						)}
					</span>
					<Button size="small" onClick={clearOperator} disabled={disabled}>
						Change
					</Button>
				</div>
				{resolved.must_rotate_pin && (
					<Text type="warning" style={{ fontSize: 12 }}>
						This operator must rotate their PIN in the Shop Floor App.
					</Text>
				)}
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
			<Text strong style={{ fontSize: '14px' }}>
				{label} <Text type="danger">*</Text>
			</Text>
			<Text type="secondary" style={{ fontSize: 12 }}>
				{emergencyMode
					? 'Select the operator, then enter the emergency PIN.'
					: 'Enter your 4-digit PIN to identify yourself.'}
			</Text>

			{emergencyMode && (
				<Select
					value={emergencyOperator}
					onChange={setEmergencyOperator}
					size="large"
					disabled={disabled}
					placeholder="Select operator"
					showSearch
					optionFilterProp="label"
					options={userData.map((user) => ({
						value: user.id,
						label: user.full_name || user.first_name,
					}))}
					style={{ width: '100%' }}
				/>
			)}

			<Input.Password
				ref={inputRef}
				value={pin}
				onChange={(e) => handlePinChange(e.target.value)}
				size="large"
				inputMode="numeric"
				maxLength={PIN_LENGTH}
				placeholder="••••"
				name="operator-pin"
				autoComplete="new-password"
				data-lpignore="true"
				data-1p-ignore="true"
				disabled={disabled || isLoading || (emergencyMode && !emergencyOperator)}
				status={error || (showError && !value) ? 'error' : ''}
				style={{ width: '100%', letterSpacing: 8, fontSize: 20 }}
			/>

			{error && (
				<Text type="danger" style={{ fontSize: 12 }}>
					{error}
				</Text>
			)}
			{showError && !value && !error && (
				<Text type="danger" style={{ fontSize: 12 }}>
					Please identify the operator to continue
				</Text>
			)}

			<div>
				{emergencyMode ? (
					<Button
						type="link"
						size="small"
						disabled={disabled}
						onClick={clearOperator}
					>
						Back to PIN entry
					</Button>
				) : (
					<Button
						type="link"
						size="small"
						disabled={disabled}
						onClick={() => {
							setEmergencyMode(true);
							setError('');
							setPin('');
						}}
					>
						Forgot PIN? Use emergency access
					</Button>
				)}
			</div>
		</div>
	);
};

export default OperatorPinField;
